import type { GeodataGroup, GeoType, WizardConfigurationState } from "../../WizardContainer";
import type { PropertyMappings } from "../../../../../shared/PropertyMappings.types";
import type { User } from "../../../../../../../models";
import type { Job, Company, Deal, Contact, Branch, Grower } from "../../../../../../../models";

/**
 * Props for the GeoTypeSelection step component
 */
export interface GeoTypeSelectionProps {
	/** Geodata groups to assign geo-types to */
	geodataGroups: GeodataGroup[];
	/** Current geo-type selections per group (groupId -> type or null) */
	geoTypeSelections: Record<string, GeoType | null>;
	/** Groups that have been excluded from processing */
	excludedGroupIds: Set<string>;
	/** Groups currently selected for map preview */
	selectedGroupIdsForMap: string[];
	/** Property mappings per group */
	propertyMappings: Record<string, PropertyMappings>;
	/** Full configuration state for job creation */
	configuration: WizardConfigurationState;
	/** Callback when user updates geo-type for a group */
	onGeoTypeChange: (groupId: string, geoType: GeoType | null) => void;
	/** Callback when user applies geo-type to all polygon or point groups */
	onApplyToAll: (geometryType: "polygon" | "point", geoType: GeoType) => void;
	/** Callback when user applies geo-type to selected groups */
	onApplyToSelected: (geoType: GeoType) => void;
	/** Callback when user excludes a group */
	onExcludeGroup: (groupId: string) => void;
	/** Callback when user includes a previously excluded group */
	onIncludeGroup: (groupId: string) => void;
	/** Callback when user changes map selection */
	onMapSelectionChange: (groupIds: string[]) => void;
	/** Callback when user clicks Previous */
	onPrevious: () => void;
	/** Callback when user clicks Next (triggers job creation) */
	onNext: (jobs: Job[]) => Promise<void>;
	/** Company configuration */
	company?: Company | null;
	/** All available deals for job creation */
	allDeals?: Deal[];
	/** Available salespersons for job creation */
	salespersons?: Contact[];
	/** Available branches for job creation */
	branches?: Branch[];
	/** Available growers for job creation */
	growers?: Grower[];
	/** Current user for job creation */
	user?: User;
}

/**
 * ViewModel interface for GeoTypeSelection step
 */
export interface GeoTypeSelectionViewModel {
	// Computed values
	/** Groups filtered to show only non-excluded */
	activeGroups: GeodataGroup[];
	/** Groups that have been excluded */
	excludedGroups: GeodataGroup[];
	/** Polygon groups (for Apply to All) */
	polygonGroups: GeodataGroup[];
	/** Point groups (for Apply to All) */
	pointGroups: GeodataGroup[];
	/** Whether any groups are selected for map */
	hasMapSelection: boolean;
	/** Geometry type of currently selected groups (null if mixed or none) */
	selectedGeometryType: "polygon" | "point" | null;
	/** Number of selected polygon groups */
	selectedPolygonCount: number;
	/** Number of selected point groups */
	selectedPointCount: number;
	/** Whether the user can proceed (all active groups have geo-types and at least one boundary exists) */
	canProceed: boolean;
	/** Reason why user cannot proceed (for disabled tooltip) */
	cannotProceedReason: string | null;
	/** Whether job creation is in progress */
	isCreatingJobs: boolean;
	/** Total number of active groups */
	totalActiveGroups: number;
	/** Total number of excluded groups */
	totalExcludedGroups: number;
	/** Number of groups with assigned geo-types */
	assignedCount: number;
	/** Number of groups without assigned geo-types */
	unassignedCount: number;
	/** Whether there is at least one boundary group */
	hasBoundaryGroup: boolean;

	// Handlers
	/** Handle geo-type change for a single group */
	handleGeoTypeChange: (groupId: string, geoType: GeoType | null) => void;
	/** Handle "All polygons are..." button */
	handleApplyToAllPolygons: (geoType: GeoType) => void;
	/** Handle "All points are..." button */
	handleApplyToAllPoints: (geoType: GeoType) => void;
	/** Handle "Apply to Selected" button */
	handleApplyToSelected: (geoType: GeoType) => void;
	/** Handle checkbox toggle for map selection */
	handleToggleMapSelection: (groupId: string) => void;
	/** Handle select all for map */
	handleSelectAllForMap: () => void;
	/** Handle deselect all for map */
	handleDeselectAllForMap: () => void;
	/** Handle exclude group (trashcan) */
	handleExcludeGroup: (groupId: string) => void;
	/** Handle include group (restore from excluded) */
	handleIncludeGroup: (groupId: string) => void;
	/** Handle Previous button */
	handlePrevious: () => void;
	/** Handle Next button (triggers job creation) */
	handleNext: () => Promise<void>;
}

/**
 * Valid geo-type options for polygon geometry
 */
export const POLYGON_GEO_TYPES: GeoType[] = ["Boundary", "Zones"];

/**
 * Valid geo-type options for point geometry
 */
export const POINT_GEO_TYPES: GeoType[] = ["Centroids", "Cores"];

/**
 * Get valid geo-types for a given geometry type
 */
export function getValidGeoTypes(geometryType: "polygon" | "point"): GeoType[] {
	return geometryType === "polygon" ? POLYGON_GEO_TYPES : POINT_GEO_TYPES;
}
